// tests/e2e(tsconfig.e2e,只挂 node types)也会 import 本文件,import.meta.env 的类型要自带。
/// <reference types="vite/client" />
import type { Brand, BrandModules } from "./types";

/**
 * OEM W5 私有化构建开关:`VITE_PRIVATE=1 vite build`(私有化镜像用 Dockerfile.private)。
 * 开着时:兜底品牌 / 首屏一律 NEUTRAL、直营白名单清空(每次等 /public/brand 或读缓存),
 * 且 DEFAULT_BRAND 的「销帮」字面量在产物里被整段摇掉(下面的三元在构建期折叠成常量)。
 * ★判据只写 `import.meta.env.VITE_PRIVATE === "1"` 这一种形状:Vite 构建期替换成字面量后才能折叠。
 */
export const IS_PRIVATE_BUILD: boolean = import.meta.env.VITE_PRIVATE === "1";

export const NEUTRAL_MODULES: BrandModules = {
  outboundCall: false,
  legacyGeo: false,
  comicDrama: false,
  computeCenter: false,
  geoAdmin: false,
};

/**
 * 中性兜底 —— 渠道域名上拉品牌失败时用,**绝不能闪出「销帮」**。
 * 无 logo、无版权行、外链模块全关。人设称呼沿用通用默认「军师」;名字位用中性「智能助手」(「小阳」是直营人设,1004 F1b)。
 */
export const NEUTRAL_BRAND: Brand = {
  key: "neutral",
  partnerId: null,
  name: "AI 营销平台",
  subtitle: "企业智能管理平台",
  productName: "AI 营销平台",
  shortName: "平台",
  shellName: "AI 营销平台",
  orgName: "",
  copyright: "",
  description: "AI 营销平台",
  logoUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  primaryColor: null,
  secondaryColor: null,
  icp: "",
  policeNo: "",
  termsUrl: "",
  privacyUrl: "",
  legal: {
    company: "平台运营方",
    address: "以服务合同约定为准",
    product: "AI 营销平台",
  },
  support: { text: "", phone: "", qrUrl: "" },
  // 1004 F1b:「小阳」是直营人设,渠道没设助手名时用中性默认(与后端 PartnerBrand.PARTNER_DEFAULT_ASSISTANT_NAME 同值)
  persona: { title: "军师", name: "智能助手" },
  advisorGreeting: "",
  loginSlogan: "",
  modules: NEUTRAL_MODULES,
  loginOrigin: "",
};

/**
 * 直营品牌 —— **现有字样逐字搬来**,上线后直营页面零变化(设计稿 C-A 验收)。
 * 改这里 = 全站生效(侧栏 / 登录页 / 协议 / 标题 / 看板副标 …)。
 */
const DIRECT_BRAND: Brand = {
  key: "default",
  partnerId: null,
  name: "销帮GEO AI",
  subtitle: "企业智能管理平台",
  productName: "销帮AI智脑",
  shortName: "智脑",
  shellName: "营销智脑",
  orgName: "销帮",
  copyright: "© {year} 销帮AI智脑 · 企业智能管理平台",
  description: "销帮AI智脑管理平台",
  logoUrl: null,
  logoDarkUrl: null,
  // 1101 用户:浏览器标签页图标换成我们的 logo(L3 军师羽扇,与官网同款),只给直营;渠道/中性仍走 html 里的兜底图标
  faviconUrl: "/zhinao-favicon.svg",
  primaryColor: null,
  secondaryColor: null,
  icp: "",
  policeNo: "",
  termsUrl: "",
  privacyUrl: "",
  legal: {
    company: "啷个不行（上海）智能技术有限公司",
    address: "上海市徐汇区零陵路583号9楼",
    product: "销帮AI智脑",
  },
  support: { text: "", phone: "", qrUrl: "" },
  persona: { title: "军师", name: "小阳" },
  advisorGreeting: "",
  loginSlogan: "",
  modules: {
    outboundCall: true,
    legacyGeo: true,
    comicDrama: true,
    computeCenter: true,
    geoAdmin: true,
  },
  loginOrigin: "https://gscq.xbwhynot.com",
  titles: {
    main: "AI智脑管理平台",
    platform: "AI智脑管理平台",
    tenant: "AI智脑管理平台",
    "wecom-h5": "AI智脑陪练",
    advisor: "经营军师",
    voice: "语音陪练",
  },
  locales: {
    en: {
      name: "Xiaobang GEO AI",
      subtitle: "Enterprise AI Platform",
      productName: "Xiaobang AI Brain",
      shellName: "Marketing Brain",
      copyright: "© {year} Xiaobang AI Brain · Enterprise AI Platform",
    },
  },
};

/**
 * 对外的「system 品牌」:SaaS 构建 = 直营逐字(DIRECT_BRAND);私有化构建 = NEUTRAL 口径但身份仍是 system
 * (key "default",登录口径同直营)。私有化产物里 DIRECT_BRAND 无人引用 → 被摇掉,产物里 grep 不到「销帮」。
 */
export const DEFAULT_BRAND: Brand =
  import.meta.env.VITE_PRIVATE === "1" ? { ...NEUTRAL_BRAND, key: "default" } : DIRECT_BRAND;
